import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import type { Profile } from '@isbd/shared';
import { getOAuthRedirectUrl } from '@/lib/nativeApp';
import { supabase } from '@/lib/supabase';

interface AuthState {
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  profileLoading: boolean;
  signIn: (email: string, password: string) => Promise<Session>;
  signUp: (input: SignUpInput) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  isStaff: boolean;
  isAdmin: boolean;
}

export interface SignUpInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  countryCode?: string;
  referralToken?: string;
}

const AuthContext = createContext<AuthState | null>(null);

async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();
  if (error) {
    console.error(error);
    return null;
  }
  return data as Profile | null;
}

/** Diffère le travail async hors du callback auth pour éviter un deadlock du lock Supabase (surtout WebView mobile). */
function deferAuthSideEffect(task: () => void) {
  window.setTimeout(task, 0);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  /** true tant que la session existe mais le profil n’est pas encore résolu */
  const [profileLoading, setProfileLoading] = useState(false);

  const refreshProfile = useCallback(async () => {
    const {
      data: { session: current },
    } = await supabase.auth.getSession();
    const uid = current?.user?.id;
    if (!uid) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }
    setProfileLoading(true);
    try {
      setProfile(await fetchProfile(uid));
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
      if (data.session?.user) {
        const userId = data.session.user.id;
        setProfileLoading(true);
        deferAuthSideEffect(() => {
          void fetchProfile(userId).then((p) => {
            if (!mounted) return;
            setProfile(p);
            setProfileLoading(false);
            void supabase.rpc('touch_last_seen');
          });
        });
      } else {
        setProfileLoading(false);
      }
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      if (event === 'PASSWORD_RECOVERY') {
        try {
          sessionStorage.setItem('isbd_password_recovery', '1');
        } catch {
          /* ignore */
        }
      }
      setSession(next);
      setLoading(false);
      if (next?.user) {
        const userId = next.user.id;
        setProfileLoading(true);
        deferAuthSideEffect(() => {
          void fetchProfile(userId).then((p) => {
            if (!mounted) return;
            setProfile(p);
            setProfileLoading(false);
            void supabase.rpc('touch_last_seen');
          });
        });
      } else {
        setProfile(null);
        setProfileLoading(false);
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (!data.session) throw new Error('Session introuvable après connexion.');
    setSession(data.session);
    setProfileLoading(true);
    try {
      const p = await fetchProfile(data.session.user.id);
      setProfile(p);
      return data.session;
    } finally {
      setProfileLoading(false);
    }
  }, []);

  const signUp = useCallback(async (input: SignUpInput) => {
    // APK : https://localhost/auth/callback (intent → app). Web : origine courante.
    const emailRedirectTo = getOAuthRedirectUrl();
    const { error } = await supabase.auth.signUp({
      email: input.email,
      password: input.password,
      options: {
        emailRedirectTo,
        data: {
          first_name: input.firstName,
          last_name: input.lastName,
          phone_number: input.phoneNumber ?? '',
          country_code: input.countryCode ?? '',
          referral_token: input.referralToken ?? '',
        },
      },
    });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setSession(null);
    setProfile(null);
    setProfileLoading(false);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      session,
      profile,
      loading,
      profileLoading,
      signIn,
      signUp,
      signOut,
      refreshProfile,
      isStaff: profile?.role === 'admin' || profile?.role === 'advisor' || profile?.role === 'jury',
      isAdmin: profile?.role === 'admin',
    }),
    [session, profile, loading, profileLoading, signIn, signUp, signOut, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans AuthProvider');
  return ctx;
}
